import React, { useEffect, useState } from 'react';
import { Modal, Button, Checkbox } from 'antd';
import { X } from 'lucide-react';
import type { UserUpdateRequest } from '@shared/services/users.service';
import type { PersonnelDetail } from '@/types/personnelDetail';
import { UpdateRequestPreview } from './UpdateRequestPreview';
import styles from './ApproveConfirmModal.module.css';

export interface ApproveConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (confirmClearingCollections: boolean) => Promise<void>;
  request: UserUpdateRequest | null;
  currentData: PersonnelDetail;
  currentValues: Record<string, unknown>;
  departmentNames: Record<number, string>;
  idCards: Array<{ id: number; documentType: string; fileUrl?: string; status?: string }>;
}

export const ApproveConfirmModal: React.FC<ApproveConfirmModalProps> = ({
  open,
  onClose,
  onConfirm,
  request,
  currentData,
  currentValues,
  departmentNames,
  idCards,
}) => {
  const [loading, setLoading] = useState(false);
  const [confirmedClear, setConfirmedClear] = useState(false);

  useEffect(() => {
    setConfirmedClear(false);
  }, [request?.id, open]);

  const updateData = request?.updateData || {};
  const collectionsToClear = [
    Array.isArray(updateData.educationHistories) && updateData.educationHistories.length === 0 ? 'học vấn' : null,
    Array.isArray(updateData.familyDependents) && updateData.familyDependents.length === 0 ? 'người phụ thuộc' : null,
    Array.isArray(updateData.certificates) && updateData.certificates.length === 0 ? 'chứng chỉ' : null,
  ].filter((label): label is string => typeof label === 'string');

  const handleConfirm = async () => {
    if (collectionsToClear.length > 0 && !confirmedClear) return;
    setLoading(true);
    try {
      await onConfirm(confirmedClear);
      setConfirmedClear(false);
      onClose();
    } catch (error) {
      console.error('Approve failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setConfirmedClear(false);
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      closeIcon={<X size={20} />}
      className={styles.modal}
      width={760}
      destroyOnHidden
    >
      <div className={styles.modalContent}>
        <h2 className={styles.modalTitle}>Xác nhận duyệt chỉnh sửa?</h2>
        
        <p className={styles.modalDescription}>
          Kiểm tra nội dung bên dưới trước khi duyệt. Thay đổi sẽ được áp dụng vào hồ sơ nhân viên.
        </p>

        {request && <UpdateRequestPreview request={request} currentData={currentData} currentValues={currentValues} departmentNames={departmentNames} idCards={idCards} />}

        {collectionsToClear.length > 0 && (
          <div className={styles.clearWarning}>
            <strong>Yêu cầu này đặt {collectionsToClear.join(', ')} thành danh sách rỗng; dữ liệu hiện có sẽ bị xóa.</strong>
            <Checkbox checked={confirmedClear} onChange={(event) => setConfirmedClear(event.target.checked)}>
              Tôi đã kiểm tra và đồng ý xóa các danh sách trên
            </Checkbox>
          </div>
        )}

        <div className={styles.modalFooter}>
          <Button
            type="default"
            onClick={handleCancel}
            className={styles.cancelButton}
          >
            Hủy
          </Button>
          <Button
            type="primary"
            onClick={handleConfirm}
            loading={loading}
            disabled={!request || (collectionsToClear.length > 0 && !confirmedClear)}
            className={styles.confirmButton}
          >
            Duyệt
          </Button>
        </div>
      </div>
    </Modal>
  );
};
